import { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Loader2, Sparkles, Search, CheckCircle2, XCircle, AlertTriangle,
  ShieldCheck, ShieldAlert, ChevronDown, Upload, RefreshCw, List,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { AllergenBadge } from "../allergens/AllergenIcons";
import { ComplianceCard } from "./ComplianceCard";
import { useImageGeneration } from "../../context/ImageGenerationContext";
import CreditCheckDialog from "../CreditCheckDialog";

const STATUS_LABELS = {
  not_analyzed: { label: "Sin analizar", color: "text-slate-400", bg: "bg-muted/50", icon: List },
  needs_review: { label: "Pendiente", color: "text-amber-700", bg: "bg-amber-50", icon: ShieldAlert },
  ai_needs_review: { label: "IA - Revisar", color: "text-amber-700", bg: "bg-amber-50", icon: ShieldAlert },
  computed: { label: "Automatico", color: "text-blue-600", bg: "bg-blue-50", icon: RefreshCw },
  ai_verified: { label: "IA Verificado", color: "text-emerald-700", bg: "bg-emerald-50", icon: ShieldCheck },
  verified: { label: "Verificado", color: "text-emerald-700", bg: "bg-emerald-50", icon: ShieldCheck },
};

export function AllergenReviewTab({ restaurantId }) {
  const { allergenGenActive } = useImageGeneration();
  const [allergenCatalog, setAllergenCatalog] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inferring, setInferring] = useState(false);
  const [inferringDish, setInferringDish] = useState(null);
  const [search, setSearch] = useState("");
  const [subTab, setSubTab] = useState("dishes");
  const [expandedDish, setExpandedDish] = useState(null);
  const [importing, setImporting] = useState(false);
  const [creditCheck, setCreditCheck] = useState(null);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [complianceKey, setComplianceKey] = useState(0);

  const catalogMap = {};
  allergenCatalog.forEach(a => { catalogMap[a.allergen_id] = a; });

  const fetchData = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const [catalog, allDishes, revs] = await Promise.all([
        api.getGlobalAllergens(),
        api.getDishesAllergenStatus(restaurantId),
        api.getAllergenReviews(restaurantId, "pending"),
      ]);
      setAllergenCatalog(catalog);
      setDishes(allDishes);
      setReviews(revs);
    } catch {
      toast.error("Error al cargar datos de alergenos");
    }
    setLoading(false);
  }, [restaurantId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleBatchInfer = () => {
    // Open credit check dialog first
    const count = dishes.length;
    setCreditCheck({
      action: "allergen_ai",
      count,
      label: "analisis de alergenos",
      onConfirm: async () => {
        setCreditCheck(null);
        setInferring(true);
        try {
          const result = await api.aiInferAllergensBatch(restaurantId);
          if (result.error) {
            toast.error(result.message, { duration: 6000 });
          } else {
            toast.success(result.message);
          }
        } catch (e) {
          toast.error(e?.message || "Error al iniciar analisis IA");
        }
        setInferring(false);
      },
    });
  };

  const handleSingleInfer = async (dishId) => {
    setInferringDish(dishId);
    try {
      const result = await api.aiInferDishAllergens(restaurantId, dishId);
      if (result.error) {
        toast.error(result.error);
      } else {
        toast.success(`Analizado: ${result.dish_name}`);
        // Update local state
        setDishes(prev => prev.map(d =>
          d.dish_id === dishId
            ? { ...d, contains: result.contains, may_contain: result.may_contain, traces: result.traces, confidence_score: result.confidence_score, review_status: result.review_status, last_computed_at: result.last_computed_at }
            : d
        ));
      }
    } catch (e) {
      if (e?.status === 402) toast.error("Sin creditos suficientes");
      else toast.error(e?.message || "Error al analizar");
    }
    setInferringDish(null);
  };

  const handleApprove = async (dishId) => {
    try {
      await api.approveAllergenReview(restaurantId, dishId);
      toast.success("Alergenos aprobados");
      setReviews(prev => prev.filter(r => r.dish_id !== dishId));
      setDishes(prev => prev.map(d => d.dish_id === dishId ? { ...d, review_status: "verified" } : d));
    } catch { toast.error("Error al aprobar"); }
  };

  const handleReject = async (dishId) => {
    try {
      await api.rejectAllergenReview(restaurantId, dishId);
      toast.success("Inferencia rechazada");
      setReviews(prev => prev.filter(r => r.dish_id !== dishId));
      setDishes(prev => prev.map(d => d.dish_id === dishId ? { ...d, review_status: "not_analyzed", contains: [], may_contain: [], traces: [] } : d));
    } catch { toast.error("Error al rechazar"); }
  };

  const handleBulkApprove = async () => {
    setBulkApproving(true);
    try {
      const result = await api.bulkApproveHighConfidenceAllergens(restaurantId);
      toast.success(result.message || `${result.approved} plato(s) verificados`);
      // Update local dishes state
      setDishes(prev => prev.map(d =>
        d.review_status === "ai_verified" ? { ...d, review_status: "verified" } : d
      ));
      // Trigger ComplianceCard reload
      setComplianceKey(k => k + 1);
    } catch (e) {
      toast.error(e?.message || "Error al aprobar en bloque");
    } finally {
      setBulkApproving(false);
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const lines = text.split("\n").filter(l => l.trim());
      if (lines.length < 2) { toast.error("CSV vacio"); setImporting(false); return; }
      const header = lines[0].split(/[,;\t]/).map(h => h.trim().toLowerCase());
      const nameIdx = header.findIndex(h => h.includes("plato") || h.includes("dish") || h.includes("nombre"));
      const containsIdx = header.findIndex(h => h.includes("contiene") || h.includes("contains"));
      const mayIdx = header.findIndex(h => h.includes("puede") || h.includes("may"));
      if (nameIdx < 0) { toast.error("CSV debe tener columna 'plato' o 'nombre'"); setImporting(false); return; }
      const items = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(/[,;\t]/).map(c => c.trim());
        if (!cols[nameIdx]) continue;
        items.push({
          dish_name: cols[nameIdx],
          contains: containsIdx >= 0 && cols[containsIdx] ? cols[containsIdx].split("|").map(s => s.trim()) : [],
          may_contain: mayIdx >= 0 && cols[mayIdx] ? cols[mayIdx].split("|").map(s => s.trim()) : [],
        });
      }
      const result = await api.importAllergens(restaurantId, { dishes: items });
      toast.success(result.message);
      if (result.conflicts?.length) toast.warning(`${result.conflicts.length} conflictos detectados`);
      fetchData();
    } catch (err) { toast.error(err?.message || "Error al importar CSV"); }
    setImporting(false);
    e.target.value = "";
  };

  // Filter dishes by search
  const filteredDishes = dishes.filter(d =>
    !search || d.name?.toLowerCase().includes(search.toLowerCase())
  );

  const analyzedCount = dishes.filter(d => d.review_status !== "not_analyzed").length;
  const pendingReviewCount = reviews.length;
  const highConfidenceCount = dishes.filter(d => d.review_status === "ai_verified").length;

  if (loading && dishes.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="allergen-review-tab">
      {/* Compliance summary + PDF download (EU 1169/2011) */}
      <ComplianceCard restaurantId={restaurantId} reloadKey={complianceKey} />

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex gap-2 items-center flex-wrap">
          <Button onClick={handleBatchInfer} disabled={inferring || allergenGenActive} className="gap-2" data-testid="batch-infer-btn">
            {inferring || allergenGenActive ? <><Loader2 className="w-4 h-4 animate-spin" />Analizando...</> : <><Sparkles className="w-4 h-4" />Analizar todos con IA</>}
          </Button>
          <label className="cursor-pointer">
            <input type="file" accept=".csv,.txt" onChange={handleImportCSV} className="hidden" data-testid="import-csv-input" />
            <Button variant="outline" className="gap-2 pointer-events-none" disabled={importing} asChild>
              <span>{importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Importar CSV</span>
            </Button>
          </label>
          {highConfidenceCount > 0 && (
            <Button
              variant="outline"
              className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-400 dark:hover:bg-emerald-900/20"
              onClick={handleBulkApprove}
              disabled={bulkApproving}
              data-testid="bulk-approve-btn"
            >
              {bulkApproving
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <ShieldCheck className="w-4 h-4" />}
              Aprobar alta confianza ({highConfidenceCount})
            </Button>
          )}
        </div>
        <div className="flex gap-2 items-center text-xs text-muted-foreground">
          <Badge variant="secondary">{analyzedCount}/{dishes.length} analizados</Badge>
          {pendingReviewCount > 0 && (
            <Badge variant="destructive" className="bg-amber-100 text-amber-700 border-amber-200">{pendingReviewCount} por revisar</Badge>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder="Buscar plato..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="allergen-search" />
      </div>

      {/* Sub-tabs */}
      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList>
          <TabsTrigger value="dishes" className="gap-1.5" data-testid="subtab-dishes">
            <List className="w-3.5 h-3.5" /> Todos los platos ({filteredDishes.length})
          </TabsTrigger>
          <TabsTrigger value="review" className="gap-1.5" data-testid="subtab-review">
            <ShieldAlert className="w-3.5 h-3.5" /> Cola de revision
            {pendingReviewCount > 0 && <Badge className="ml-1 text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700 border-amber-200">{pendingReviewCount}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* All dishes tab */}
        <TabsContent value="dishes">
          {filteredDishes.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>No se encontraron platos{search ? ` para "${search}"` : ""}</p>
            </div>
          ) : (
            <div className="divide-y border rounded-lg overflow-hidden bg-card" data-testid="dishes-allergen-list">
              {filteredDishes.map(dish => {
                const st = STATUS_LABELS[dish.review_status] || STATUS_LABELS.not_analyzed;
                const StIcon = st.icon;
                const isExpanded = expandedDish === dish.dish_id;
                const hasAllergens = (dish.contains?.length || 0) + (dish.may_contain?.length || 0) > 0;
                return (
                  <div key={dish.dish_id} data-testid={`dish-row-${dish.dish_id}`}>
                    <div className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors">
                      {/* Status */}
                      <StIcon className={`w-4 h-4 shrink-0 ${st.color}`} />

                      {/* Dish name */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{dish.name}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${st.bg} ${st.color}`}>{st.label}</span>
                          {dish.confidence_score > 0 && (
                            <span className="text-[10px] text-muted-foreground">{Math.round(dish.confidence_score * 100)}%</span>
                          )}
                          {/* Allergen badges inline */}
                          {hasAllergens && (
                            <div className="flex gap-0.5">
                              {(dish.contains || []).slice(0, 6).map(id => (
                                <AllergenBadge key={id} allergen={catalogMap[id]} size="xs" severity="contains" />
                              ))}
                              {(dish.may_contain || []).slice(0, 3).map(id => (
                                <AllergenBadge key={id} allergen={catalogMap[id]} size="xs" severity="may_contain" />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Button
                          size="sm" variant="outline"
                          className="h-7 text-xs gap-1"
                          disabled={inferringDish === dish.dish_id}
                          onClick={() => handleSingleInfer(dish.dish_id)}
                          data-testid={`infer-btn-${dish.dish_id}`}
                        >
                          {inferringDish === dish.dish_id
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <Sparkles className="w-3 h-3" />
                          }
                          {dish.review_status === "not_analyzed" ? "Analizar" : "Re-analizar"}
                        </Button>
                        {hasAllergens && (
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setExpandedDish(isExpanded ? null : dish.dish_id)}>
                            <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Expanded detail */}
                    {isExpanded && hasAllergens && (
                      <div className="px-4 pb-4 pt-1 bg-muted/20 border-t space-y-2">
                        {dish.contains?.length > 0 && (
                          <div>
                            <p className="text-[10px] font-bold uppercase text-red-700 mb-1">Contiene</p>
                            <div className="flex flex-wrap gap-1.5">
                              {(Array.isArray(dish.contains) ? dish.contains : []).map(id => (
                                <span key={id} className="text-xs bg-red-50 border border-red-200 rounded-full px-2 py-0.5 text-red-800">
                                  {catalogMap[id]?.name || id}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {dish.may_contain?.length > 0 && (
                          <div>
                            <p className="text-[10px] font-bold uppercase text-amber-700 mb-1">Puede contener</p>
                            <div className="flex flex-wrap gap-1.5">
                              {(Array.isArray(dish.may_contain) ? dish.may_contain : []).map(id => (
                                <span key={id} className="text-xs bg-amber-50 border border-amber-200 border-dashed rounded-full px-2 py-0.5 text-amber-800">
                                  {catalogMap[id]?.name || id}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {dish.last_computed_at && (
                          <p className="text-[10px] text-muted-foreground pt-1">
                            Ultimo analisis: {new Date(dish.last_computed_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Review queue tab */}
        <TabsContent value="review">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800 flex items-start gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Cola de revision IA</p>
              <p className="mt-0.5 text-amber-700">Inferencias con confianza menor al 80%. Aprueba o rechaza antes de publicar.</p>
            </div>
          </div>

          {reviews.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground" data-testid="review-empty">
              <ShieldCheck className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No hay revisiones pendientes</p>
            </div>
          ) : (
            <div className="divide-y border rounded-lg overflow-hidden bg-card">
              {reviews.map(review => {
                const aiResult = review.ai_result || {};
                const isExp = expandedDish === `rev_${review.dish_id}`;
                return (
                  <div key={review.dish_id} data-testid={`review-item-${review.dish_id}`}>
                    <div className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors">
                      <ShieldAlert className="w-5 h-5 text-amber-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{review.dish_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-[10px]">{Math.round((review.confidence || 0) * 100)}% confianza</Badge>
                          <div className="flex gap-0.5">
                            {(aiResult.contains || []).slice(0, 5).map(id => <AllergenBadge key={id} allergen={catalogMap[id]} size="xs" severity="contains" />)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => handleApprove(review.dish_id)} data-testid={`approve-${review.dish_id}`}>
                          <CheckCircle2 className="w-3 h-3" /> Aprobar
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-red-500 border-red-200 hover:bg-red-50" onClick={() => handleReject(review.dish_id)} data-testid={`reject-${review.dish_id}`}>
                          <XCircle className="w-3 h-3" /> Rechazar
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setExpandedDish(isExp ? null : `rev_${review.dish_id}`)}>
                          <ChevronDown className={`w-4 h-4 transition-transform ${isExp ? "rotate-180" : ""}`} />
                        </Button>
                      </div>
                    </div>
                    {isExp && (
                      <div className="px-4 pb-4 pt-1 bg-muted/20 border-t space-y-2">
                        {aiResult.contains?.length > 0 && (
                          <div>
                            <p className="text-[10px] font-bold uppercase text-red-700 mb-1">Contiene</p>
                            <div className="flex flex-wrap gap-1.5">{(Array.isArray(aiResult.contains) ? aiResult.contains : []).map(id => <span key={id} className="text-xs bg-red-50 border border-red-200 rounded-full px-2 py-0.5 text-red-800">{catalogMap[id]?.name || id}</span>)}</div>
                          </div>
                        )}
                        {aiResult.may_contain?.length > 0 && (
                          <div>
                            <p className="text-[10px] font-bold uppercase text-amber-700 mb-1">Puede contener</p>
                            <div className="flex flex-wrap gap-1.5">{(Array.isArray(aiResult.may_contain) ? aiResult.may_contain : []).map(id => <span key={id} className="text-xs bg-amber-50 border border-amber-200 border-dashed rounded-full px-2 py-0.5 text-amber-800">{catalogMap[id]?.name || id}</span>)}</div>
                          </div>
                        )}
                        {aiResult.explanations?.length > 0 && (
                          <div className="mt-2">
                            <p className="text-[10px] font-bold uppercase text-muted-foreground mb-1">Razonamiento IA</p>
                            {(Array.isArray(aiResult.explanations) ? aiResult.explanations : []).map((e, i) => (
                              <p key={i} className="text-xs text-muted-foreground"><span className="font-medium">{catalogMap[e.allergen_id]?.name || e.allergen_id}</span>: {e.source}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Credit Check Dialog for batch allergen inference */}
      {creditCheck && (
        <CreditCheckDialog
          open={!!creditCheck}
          onClose={() => setCreditCheck(null)}
          onConfirm={creditCheck.onConfirm}
          restaurantId={restaurantId}
          action={creditCheck.action}
          count={creditCheck.count}
          actionLabel={creditCheck.label}
        />
      )}
    </div>
  );
}
